import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  Chip,
  Collapse,
} from '@mui/material';
import {
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Remove as PartialIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
} from '@mui/icons-material';
import PromptService from '../services/PromptService';

const colors = {
  surface: '#1e1e1e',
  surfaceElevated: '#2d2d2d',
  border: '#404040',
  textPrimary: '#e0e0e0',
  textSecondary: '#a0a0a0',
  textMuted: '#666',
  success: '#22c55e',
  warning: '#f59e0b',
  error: '#ef4444',
};

const TestRunRating = ({
  testRunId,
  currentRating,
  onRatingChange,
  latencyMs,
  tokenUsage,
  modelName,
}) => {
  const [rating, setRating] = useState(currentRating || 'PENDING');
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleRate = async (newRating) => {
    if (!testRunId) return;

    setSaving(true);
    try {
      await PromptService.ratePromptTestRun(testRunId, newRating, notes);
      setRating(newRating);
      if (onRatingChange) {
        onRatingChange(newRating);
      }
    } catch (err) {
      console.error('Failed to rate test run:', err);
    } finally {
      setSaving(false);
    }
  };

  const getRatingColor = (r) => {
    switch (r) {
      case 'PASS': return colors.success;
      case 'FAIL': return colors.error;
      case 'PARTIAL': return colors.warning;
      default: return colors.textMuted;
    }
  };

  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${colors.border}` }}>
      {/* Metadata Row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
        {modelName && (
          <Chip
            label={modelName}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.7rem',
              bgcolor: 'rgba(139, 92, 246, 0.2)',
              color: '#8b5cf6',
            }}
          />
        )}
        {latencyMs && (
          <Typography sx={{ color: colors.textMuted, fontSize: '0.75rem' }}>
            Latency: {latencyMs}ms
          </Typography>
        )}
        {tokenUsage && (
          <Typography sx={{ color: colors.textMuted, fontSize: '0.75rem' }}>
            Tokens: {tokenUsage.input || 0} in / {tokenUsage.output || 0} out
          </Typography>
        )}
      </Box>

      {/* Rating Row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ color: colors.textSecondary, fontSize: '0.8rem', mr: 1 }}>
          Rate this response:
        </Typography>

        <Tooltip title="Pass - Response is correct">
          <IconButton
            size="small"
            onClick={() => handleRate('PASS')}
            disabled={saving || !testRunId}
            sx={{
              color: rating === 'PASS' ? colors.success : colors.textMuted,
              bgcolor: rating === 'PASS' ? 'rgba(34, 197, 94, 0.1)' : 'transparent',
              '&:hover': { bgcolor: 'rgba(34, 197, 94, 0.2)' },
            }}
          >
            <ThumbUpIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Partial - Response is partially correct">
          <IconButton
            size="small"
            onClick={() => handleRate('PARTIAL')}
            disabled={saving || !testRunId}
            sx={{
              color: rating === 'PARTIAL' ? colors.warning : colors.textMuted,
              bgcolor: rating === 'PARTIAL' ? 'rgba(245, 158, 11, 0.1)' : 'transparent',
              '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.2)' },
            }}
          >
            <PartialIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Fail - Response is incorrect">
          <IconButton
            size="small"
            onClick={() => handleRate('FAIL')}
            disabled={saving || !testRunId}
            sx={{
              color: rating === 'FAIL' ? colors.error : colors.textMuted,
              bgcolor: rating === 'FAIL' ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
              '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.2)' },
            }}
          >
            <ThumbDownIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {rating !== 'PENDING' && (
          <Chip
            label={rating}
            size="small"
            sx={{
              ml: 1,
              height: 20,
              fontSize: '0.7rem',
              bgcolor: `${getRatingColor(rating)}22`,
              color: getRatingColor(rating),
              border: `1px solid ${getRatingColor(rating)}`,
            }}
          />
        )}

        <IconButton
          size="small"
          onClick={() => setShowNotes(!showNotes)}
          sx={{ ml: 'auto', color: colors.textMuted }}
        >
          {showNotes ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Notes Section */}
      <Collapse in={showNotes}>
        <TextField
          fullWidth
          size="small"
          multiline
          rows={2}
          placeholder="Add notes about this test run..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{
            mt: 1,
            '& .MuiOutlinedInput-root': {
              bgcolor: colors.surfaceElevated,
              '& fieldset': { borderColor: colors.border },
            },
            '& .MuiInputBase-input': {
              color: colors.textPrimary,
              fontSize: '0.8rem',
            },
          }}
        />
      </Collapse>

      {!testRunId && (
        <Typography sx={{ color: colors.textMuted, fontSize: '0.7rem', mt: 1, fontStyle: 'italic' }}>
          Test runs are saved when using Simulate. Rating will be available after simulation.
        </Typography>
      )}
    </Box>
  );
};

export default TestRunRating;
